import { getDefaultRankingSubcategory } from './sports';

export const RANKING_EVAL_TYPES = [
  { id: 'self-evaluation', label: 'By Self', shortLabel: 'Self', legacyLabel: 'Self Evaluation' },
  { id: 'coach-academy', label: 'By Coach/Academy', shortLabel: 'Coach', legacyLabel: 'Coach/Academy Evaluation' },
  { id: 'practical-test', label: 'By EncycGames', shortLabel: 'EncycGames', legacyLabel: 'Certificate Exam' },
] as const;

export type RankingEvalTypeId = (typeof RANKING_EVAL_TYPES)[number]['id'];

export const RANKING_SPECIAL_ROUTE_IDS = new Set([
  'submitted-ranking',
  'tournament',
  'contest',
]);

export const RANKING_CONTENT_SUBCATEGORIES = new Set([
  'strokes-shots',
  'shooting',
  'drills',
  'fitness',
]);

export const isRankingEvalTypeId = (value: string): value is RankingEvalTypeId =>
  RANKING_EVAL_TYPES.some((item) => item.id === value);

export const isRankingContentSubcategory = (value: string) =>
  RANKING_CONTENT_SUBCATEGORIES.has(value);

export const getRankingEvalLabel = (evalId: string, style: 'tab' | 'legacy' = 'tab') => {
  const match = RANKING_EVAL_TYPES.find((item) => item.id === evalId);
  if (!match) return evalId;
  return style === 'legacy' ? match.legacyLabel : match.label;
};

export const getContentTypeLabel = (sport: string, subcategory: string) => {
  if (subcategory === 'shooting' || (subcategory === 'strokes-shots' && getDefaultRankingSubcategory(sport) === 'shooting')) {
    return 'Shooting';
  }
  if (subcategory === 'strokes-shots') return 'Strokes & Shots';
  if (subcategory === 'drills') return 'Drills';
  if (subcategory === 'fitness') return 'Fitness';
  return subcategory;
};

export interface EvaluatorScoreMetrics {
  totalScore: number;
  maxScore: number;
  ratedCount: number;
  notRatedCount: number;
}

export interface ParsedRankingEvaluationRoute {
  pathSegment: string;
  evalType: RankingEvalTypeId;
  contentSubcategory: string;
  isEvaluationContent: boolean;
  isSpecialRoute: boolean;
}

export const getRankingEvaluationPathSegments = (pathname: string): string[] => {
  const marker = '/ranking-evaluation/';
  const idx = pathname.indexOf(marker);
  if (idx < 0) return [];
  return pathname.slice(idx + marker.length).split('/').filter(Boolean);
};

export const parseRankingEvaluationRoute = (
  pathname: string,
  searchParams: URLSearchParams,
  sport: string
): ParsedRankingEvaluationRoute => {
  const segments = getRankingEvaluationPathSegments(pathname);
  const pathSegment = segments[segments.length - 1] || 'strokes-shots';
  const defaultSubcategory = getDefaultRankingSubcategory(sport);

  if (segments.length >= 2 && isRankingContentSubcategory(segments[0]) && isRankingEvalTypeId(segments[1])) {
    return {
      pathSegment: segments[0],
      evalType: segments[1],
      contentSubcategory: segments[0],
      isEvaluationContent: true,
      isSpecialRoute: false,
    };
  }

  if (RANKING_SPECIAL_ROUTE_IDS.has(pathSegment) || RANKING_SPECIAL_ROUTE_IDS.has(segments[0] || '')) {
    const special = RANKING_SPECIAL_ROUTE_IDS.has(segments[0] || '') ? segments[0] : pathSegment;
    return {
      pathSegment: special,
      evalType: 'self-evaluation',
      contentSubcategory: defaultSubcategory,
      isEvaluationContent: false,
      isSpecialRoute: true,
    };
  }

  if (isRankingEvalTypeId(pathSegment)) {
    return {
      pathSegment,
      evalType: pathSegment,
      contentSubcategory: searchParams.get('subcategory') || defaultSubcategory,
      isEvaluationContent: true,
      isSpecialRoute: false,
    };
  }

  if (isRankingContentSubcategory(pathSegment)) {
    const evalParam = searchParams.get('eval') || 'self-evaluation';
    return {
      pathSegment,
      evalType: isRankingEvalTypeId(evalParam) ? evalParam : 'self-evaluation',
      contentSubcategory: pathSegment,
      isEvaluationContent: true,
      isSpecialRoute: false,
    };
  }

  return {
    pathSegment,
    evalType: 'self-evaluation',
    contentSubcategory: defaultSubcategory,
    isEvaluationContent: true,
    isSpecialRoute: false,
  };
};
